import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import {
  Lightbulb,
  Warning,
  WarningCircle,
  Info,
  ArrowRight,
  TrendUp,
  TrendDown,
  Wallet,
  Receipt,
  Clock,
  CheckCircle
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Badge } from "../../components/ui/badge";
import { Link, useNavigate } from 'react-router-dom';
import { useFinanceDrill } from '../../components/reports/useFinanceDrill';
import { DataNote } from '../../components/reports/DataNote';

const KPI_DRILL = {
  revenue: { category: 'income', title: 'Monthly Revenue — income entries before VAT' },
  expenses: { category: 'expense', title: 'Monthly Expenses — expense entries' },
  net_profit: { category: 'pnl', title: 'Net Profit — revenue less expenses' },
  margin: { category: 'pnl', title: 'Profit Margin — Net Profit ÷ Revenue × 100' },
};
const KPI_LINK = { employees: '/dashboard/hr', customers: '/dashboard/contacts?type=customer', approvals: '/dashboard/approvals' };

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightsPage() {
  const { token } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { openDrill, openParty, dialogs } = useFinanceDrill();
  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState(null);
  const [kpis, setKpis] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [insightsRes, kpisRes] = await Promise.all([
        fetch(`${API}/api/analytics/insights`, { headers }),
        fetch(`${API}/api/analytics/kpis`, { headers })
      ]);
      
      if (insightsRes.ok) setInsightsData(await insightsRes.json());
      if (kpisRes.ok) setKpis(await kpisRes.json());
    } catch (err) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getSeverityConfig = (severity) => {
    const configs = {
      critical: {
        icon: WarningCircle,
        color: 'text-red-600',
        bg: 'bg-red-500/10',
        border: 'border-l-red-500',
        badge: 'bg-red-500/10 text-red-600'
      },
      warning: {
        icon: Warning,
        color: 'text-amber-600',
        bg: 'bg-amber-500/10',
        border: 'border-l-amber-500',
        badge: 'bg-amber-500/10 text-amber-600'
      },
      info: {
        icon: Info,
        color: 'text-blue-600',
        bg: 'bg-blue-500/10',
        border: 'border-l-blue-500',
        badge: 'bg-blue-500/10 text-blue-600'
      }
    };
    return configs[severity] || configs.info;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      revenue: TrendUp,
      expense: TrendDown,
      cash_flow: Wallet,
      receivables: Receipt,
      payables: Receipt,
      workflow: Clock
    };
    return icons[category] || Info;
  };

  const getKPIIcon = (trend) => {
    if (trend === 'up') return <TrendUp size={16} className="text-emerald-500" />;
    if (trend === 'down') return <TrendDown size={16} className="text-red-500" />;
    if (trend === 'warning') return <Warning size={16} className="text-amber-500" />;
    return <CheckCircle size={16} className="text-muted-foreground" />;
  };

  const formatKPIValue = (kpi) => {
    if (kpi.format === 'currency') return formatCurrency(kpi.value);
    if (kpi.format === 'percent') return `${kpi.value}%`;
    return kpi.value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="insights-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const insights = insightsData?.insights || [];
  const summary = insightsData?.summary || {};
  const kpiList = kpis?.kpis || [];

  return (
    <div className="space-y-8" data-testid="insights-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Smart Insights
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            AI-powered recommendations and alerts for your business
          </p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <Lightbulb size={18} className="mr-2" />
          Refresh Insights
        </Button>
      </motion.div>

      <DataNote />

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <div className="card-tactile rounded-lg p-4 text-center">
          <p className="text-3xl font-bold">{summary.total_insights || 0}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Total Insights</p>
        </div>
        <div className="card-tactile rounded-lg p-4 text-center border-l-4 border-l-red-500">
          <p className="text-3xl font-bold text-red-600">{summary.critical || 0}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Critical</p>
        </div>
        <div className="card-tactile rounded-lg p-4 text-center border-l-4 border-l-amber-500">
          <p className="text-3xl font-bold text-amber-600">{summary.warnings || 0}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Warnings</p>
        </div>
        <div className="card-tactile rounded-lg p-4 text-center border-l-4 border-l-blue-500">
          <p className="text-3xl font-bold text-blue-600">{summary.info || 0}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Informational</p>
        </div>
      </motion.div>

      {/* KPI Section */}
      {kpiList.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactile rounded-lg p-6"
        >
          <h3 className="font-heading text-lg font-medium mb-4">Key Performance Indicators - {kpis?.period}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {kpiList.map((kpi, idx) => (
              <button
                type="button"
                key={idx}
                onClick={() => (KPI_DRILL[kpi.key]
                  ? openDrill({ ...KPI_DRILL[kpi.key], startDate: kpis?.range?.start, endDate: kpis?.range?.end })
                  : KPI_LINK[kpi.key] && navigate(KPI_LINK[kpi.key]))}
                className="p-3 bg-muted/30 rounded-lg text-left hover:ring-2 hover:ring-primary/40 transition-shadow"
                data-testid={`kpi-card-${kpi.key || idx}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">{kpi.name}</span>
                  {getKPIIcon(kpi.trend)}
                </div>
                <p className="text-lg font-semibold">{formatKPIValue(kpi)}</p>
                <p className="text-xs text-muted-foreground truncate" title={kpi.description}>{kpi.description}</p>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Insights List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <h3 className="font-heading text-lg font-medium">Active Insights & Recommendations</h3>
        
        {insights.length === 0 ? (
          <div className="card-tactile rounded-lg p-8 text-center">
            <Lightbulb size={48} weight="duotone" className="mx-auto mb-4 text-emerald-500 opacity-50" />
            <p className="font-medium text-lg">All Clear!</p>
            <p className="text-muted-foreground mt-1">No critical issues or warnings detected. Your business is running smoothly.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {insights.map((insight, idx) => {
              const config = getSeverityConfig(insight.severity);
              const CategoryIcon = getCategoryIcon(insight.category);
              
              return (
                <motion.div
                  key={insight.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * idx }}
                  className={`card-tactile rounded-lg p-5 border-l-4 ${config.border} ${(insight.drill || insight.link) ? 'cursor-pointer hover:ring-2 hover:ring-primary/30' : ''}`}
                  onClick={() => {
                    const d = insight.drill;
                    if (d?.party) openParty(d.party);
                    else if (d) openDrill({ category: d.category, accountId: d.account_id, startDate: d.start_date, endDate: d.end_date, title: insight.title });
                    else if (insight.link) navigate(insight.link);
                  }}
                  data-testid={`insight-${insight.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2.5 rounded-lg ${config.bg}`}>
                      <config.icon size={24} weight="duotone" className={config.color} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold">{insight.title}</h4>
                        <Badge className={config.badge}>{insight.severity}</Badge>
                        <Badge variant="outline" className="text-xs">
                          <CategoryIcon size={12} className="mr-1" />
                          {insight.category}
                        </Badge>
                      </div>
                      
                      <p className="text-muted-foreground">{insight.message}</p>
                      
                      {insight.value && (
                        <div className="mt-3 flex items-center gap-4">
                          <div className="px-3 py-1.5 bg-muted/50 rounded-lg">
                            <span className="text-xs text-muted-foreground">Value: </span>
                            <span className="font-mono font-medium">{insight.category === 'workflow' ? insight.value : formatCurrency(insight.value)}</span>
                          </div>
                          {insight.change_percent && (
                            <div className={`px-3 py-1.5 rounded-lg ${insight.change_percent >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                              <span className="text-xs text-muted-foreground">Change: </span>
                              <span className={`font-mono font-medium ${insight.change_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {insight.change_percent >= 0 ? '+' : ''}{insight.change_percent}%
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {insight.recommendation && (
                        <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-dashed">
                          <div className="flex items-center gap-2 text-sm">
                            <Lightbulb size={16} className="text-amber-500" weight="fill" />
                            <span className="font-medium">Recommendation</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{insight.recommendation}</p>
                        </div>
                      )}
                      {(insight.drill || insight.link) && (
                        <p className="text-xs text-primary mt-3">Click to see the entries behind this →</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-tactile rounded-lg p-6"
      >
        <h3 className="font-heading text-lg font-medium mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/dashboard/analytics" className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
            <TrendUp size={24} className="text-emerald-500 mb-2" weight="duotone" />
            <p className="font-medium text-sm">View Analytics</p>
            <p className="text-xs text-muted-foreground">Detailed metrics</p>
          </Link>
          <Link to="/dashboard/forecasting" className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
            <Lightbulb size={24} className="text-amber-500 mb-2" weight="duotone" />
            <p className="font-medium text-sm">View Forecast</p>
            <p className="text-xs text-muted-foreground">Future projections</p>
          </Link>
          <Link to="/dashboard/cost-control" className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
            <Wallet size={24} className="text-blue-500 mb-2" weight="duotone" />
            <p className="font-medium text-sm">Cost Control</p>
            <p className="text-xs text-muted-foreground">Budget analysis</p>
          </Link>
          <Link to="/dashboard/approvals" className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
            <CheckCircle size={24} className="text-purple-500 mb-2" weight="duotone" />
            <p className="font-medium text-sm">Approvals</p>
            <p className="text-xs text-muted-foreground">Pending items</p>
          </Link>
        </div>
      </motion.div>
      {dialogs}
    </div>
  );
}
